import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { Plus, Check, X, Edit, Trash2, Search, Filter, Eye, Paperclip, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { AttachmentViewer } from '../../components/ui/AttachmentViewer';
import { vehicleExpenseService } from '../../services/vehicleExpense.service';
import { vehicleService } from '../../services/vehicle.service';
import { tripService } from '../../services/trip.service';
import { ExpenseCategory, ExpenseStatus, VehicleExpense } from '../../types';

const expenseSchema = z.object({
  vehicleId: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(1, 'Vehicle is required')
  ),
  tripId: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().optional()
  ),
  expenseDate: z.string().min(1, 'Expense date is required'),
  category: z.nativeEnum(ExpenseCategory),
  amount: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Amount must be greater than 0')
  ),
  vendor: z.string().optional(),
  receiptNumber: z.string().optional(),
  description: z.string().optional(),
  reimbursable: z.boolean().optional(),
  notes: z.string().optional(),
  // Maintenance fields
  odometerReading: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().optional()
  ),
  serviceProvider: z.string().optional(),
  serviceProviderContact: z.string().optional(),
  workPerformed: z.string().optional(),
  partsReplaced: z.string().optional(),
  nextServiceDue: z.string().optional(),
  nextServiceOdometer: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().optional()
  ),
  // Insurance/LATRA fields
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  policyNumber: z.string().optional(),
  insuranceProvider: z.string().optional(),
});

type ExpenseFormData = z.output<typeof expenseSchema>;

export const ExpenseManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<VehicleExpense | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Attachment viewer state
  const [isAttachmentViewerOpen, setIsAttachmentViewerOpen] = useState(false);
  const [viewingAttachmentExpense, setViewingAttachmentExpense] = useState<VehicleExpense | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [vehicleFilter, setVehicleFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: expenses, isLoading } = useQuery({
    queryKey: ['expenses'],
    queryFn: vehicleExpenseService.getAll,
  });

  const { data: pendingExpenses } = useQuery({
    queryKey: ['expenses', 'pending'],
    queryFn: vehicleExpenseService.getPending,
  });

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', 'active'],
    queryFn: vehicleService.getActive,
  });

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: tripService.getAll,
  });

  const form = useForm<ExpenseFormData>({
    // @ts-expect-error - Zod coerce types work correctly at runtime
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      expenseDate: new Date().toISOString().split('T')[0],
      amount: 0,
      reimbursable: false,
    },
  });

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = form;
  const selectedCategory = watch('category');

  const recordMutation = useMutation({
    mutationFn: vehicleExpenseService.recordExpense,
    onSuccess: () => {
      toast.success('Expense recorded successfully');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['vehicleROI'] });
      setIsModalOpen(false);
      reset();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to record expense');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ExpenseFormData }) => 
      vehicleExpenseService.update(id, data),
    onSuccess: () => {
      toast.success('Expense updated successfully');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      setIsModalOpen(false);
      setSelectedExpense(null);
      setIsEditMode(false);
      reset();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to update expense');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: vehicleExpenseService.delete,
    onSuccess: () => {
      toast.success('Expense deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to delete expense');
    },
  });

  const approveMutation = useMutation({
    mutationFn: vehicleExpenseService.approve,
    onSuccess: () => {
      toast.success('Expense approved');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to approve expense');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: vehicleExpenseService.reject,
    onSuccess: () => {
      toast.success('Expense rejected');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to reject expense');
    },
  });

  const onSubmit = async (data: ExpenseFormData) => {
    if (isEditMode && selectedExpense) {
      updateMutation.mutate({ id: selectedExpense.id, data });
    } else {
      // Create expense first, then upload attachment if present
      recordMutation.mutate(data, {
        onSuccess: async (createdExpense) => {
          if (attachmentFile) {
            setIsUploading(true);
            try {
              await vehicleExpenseService.uploadAttachment(createdExpense.id, attachmentFile);
              toast.success('Attachment uploaded successfully');
              queryClient.invalidateQueries({ queryKey: ['expenses'] });
            } catch (err) {
              toast.error('Expense created but attachment upload failed');
            } finally {
              setIsUploading(false);
              setAttachmentFile(null);
            }
          }
        },
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF, JPG, and PNG files are allowed');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachmentFile(file);
    }
  };

  const handleDownloadAttachment = async (expense: VehicleExpense) => {
    try {
      const blob = await vehicleExpenseService.downloadAttachment(expense.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = expense.attachmentFileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Failed to download attachment');
    }
  };

  const handleCreate = () => {
    setIsEditMode(false);
    setSelectedExpense(null);
    reset({
      expenseDate: new Date().toISOString().split('T')[0],
      amount: 0,
      reimbursable: false,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (expense: VehicleExpense) => {
    setIsEditMode(true);
    setSelectedExpense(expense);
    setValue('vehicleId', expense.vehicleId);
    setValue('tripId', expense.tripId);
    setValue('expenseDate', expense.expenseDate);
    setValue('category', expense.category);
    setValue('amount', expense.amount);
    setValue('vendor', expense.vendor || '');
    setValue('receiptNumber', expense.receiptNumber || '');
    setValue('description', expense.description || '');
    setValue('reimbursable', expense.reimbursable);
    setValue('notes', expense.notes || '');
    setIsModalOpen(true);
  };

  const handleView = (expense: VehicleExpense) => {
    setSelectedExpense(expense);
    setIsViewModalOpen(true);
  };

  const handleDelete = (expense: VehicleExpense) => {
    if (expense.status === ExpenseStatus.APPROVED || expense.status === ExpenseStatus.PAID) {
      toast.error('Cannot delete approved or paid expenses');
      return;
    }
    if (window.confirm('Are you sure you want to delete this expense?')) {
      deleteMutation.mutate(expense.id);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusVariant = (status: ExpenseStatus) => {
    switch (status) {
      case ExpenseStatus.APPROVED:
        return 'success';
      case ExpenseStatus.PENDING:
        return 'warning';
      case ExpenseStatus.REJECTED:
        return 'error';
      case ExpenseStatus.PAID:
        return 'info';
      default:
        return 'default';
    }
  };

  const categoryLabels: Record<ExpenseCategory, string> = {
    [ExpenseCategory.FUEL]: 'Fuel',
    [ExpenseCategory.MAINTENANCE]: 'Maintenance',
    [ExpenseCategory.REPAIR]: 'Repair',
    [ExpenseCategory.INSURANCE]: 'Insurance',
    [ExpenseCategory.LATRA]: 'LATRA',
    [ExpenseCategory.REGISTRATION]: 'Registration',
    [ExpenseCategory.PARKING]: 'Parking',
    [ExpenseCategory.TOLLS]: 'Tolls',
    [ExpenseCategory.CLEANING]: 'Cleaning',
    [ExpenseCategory.TIRES]: 'Tires',
    [ExpenseCategory.PARTS]: 'Parts',
    [ExpenseCategory.LABOR]: 'Labor',
    [ExpenseCategory.INSPECTION]: 'Inspection',
    [ExpenseCategory.FINES]: 'Fines',
    [ExpenseCategory.DRIVER_ALLOWANCE]: 'Driver Allowance',
    [ExpenseCategory.ACCOMMODATION]: 'Accommodation',
    [ExpenseCategory.MEALS]: 'Meals',
    [ExpenseCategory.TOLL_ROAD]: 'Toll/Road',
    [ExpenseCategory.LOADING_OFFLOADING]: 'Loading/Offloading',
    [ExpenseCategory.WEIGHBRIDGE]: 'Weighbridge',
    [ExpenseCategory.BORDER_FEES]: 'Border Fees',
    [ExpenseCategory.FUEL_ADVANCE]: 'Fuel Advance',
    [ExpenseCategory.OTHER]: 'Other',
  };

  // Filter expenses
  const filteredExpenses = expenses?.filter((expense) => {
    const matchesSearch = searchTerm === '' || 
      expense.expenseNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vehicleRegistration.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === '' || expense.category === categoryFilter;
    const matchesStatus = statusFilter === '' || expense.status === statusFilter;
    const matchesVehicle = vehicleFilter === '' || expense.vehicleId.toString() === vehicleFilter;
    
    const expenseDate = parseISO(expense.expenseDate);
    const matchesDateFrom = dateFrom === '' || expenseDate >= parseISO(dateFrom);
    const matchesDateTo = dateTo === '' || expenseDate <= parseISO(dateTo);
    
    return matchesSearch && matchesCategory && matchesStatus && matchesVehicle && matchesDateFrom && matchesDateTo;
  }) || [];

  // Calculate totals
  const totalAmount = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const approvedAmount = filteredExpenses
    .filter(e => e.status === ExpenseStatus.APPROVED || e.status === ExpenseStatus.PAID)
    .reduce((sum, e) => sum + e.amount, 0);
  const pendingAmount = filteredExpenses
    .filter(e => e.status === ExpenseStatus.PENDING)
    .reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal">Expenses</h1>
          <p className="text-gray-600">Manage all vehicle and trip expenses</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus size={16} className="mr-2" />
          Record Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-600">Total Expenses</p>
            <p className="text-2xl font-bold text-neutral-charcoal">{formatCurrency(totalAmount)}</p>
            <p className="text-xs text-gray-500">{filteredExpenses.length} records</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-600">Approved/Paid</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(approvedAmount)}</p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <p className="text-sm text-gray-600">Pending Approval</p>
            <p className="text-2xl font-bold text-yellow-600">{formatCurrency(pendingAmount)}</p>
          </div>
        </Card>
      </div>

      {/* Pending Approvals */}
      {pendingExpenses && pendingExpenses.length > 0 && (
        <Card title={`Pending Approvals (${pendingExpenses.length})`}>
          <div className="space-y-2">
            {pendingExpenses.slice(0, 5).map((expense) => (
              <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{expense.expenseNumber}</span>
                    <span className="text-sm text-gray-600">{expense.vehicleRegistration}</span>
                    <Badge variant="warning">{categoryLabels[expense.category]}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{expense.description || 'No description'}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-semibold">{formatCurrency(expense.amount)}</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => approveMutation.mutate(expense.id)}
                      disabled={approveMutation.isPending}
                    >
                      <Check size={16} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => rejectMutation.mutate(expense.id)}
                      disabled={rejectMutation.isPending}
                    >
                      <X size={16} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Filter size={16} className="text-gray-500" />
            <span className="font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Statuses</option>
              <option value={ExpenseStatus.PENDING}>Pending</option>
              <option value={ExpenseStatus.APPROVED}>Approved</option>
              <option value={ExpenseStatus.REJECTED}>Rejected</option>
              <option value={ExpenseStatus.PAID}>Paid</option>
            </select>
            <select
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Vehicles</option>
              {vehicles?.map((v) => (
                <option key={v.id} value={v.id}>{v.registrationNumber}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <div className="flex items-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('');
                  setStatusFilter('');
                  setVehicleFilter('');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Expenses Table */}
      <Card title="All Expenses">
        {isLoading && <div className="text-center py-8 text-gray-500">Loading expenses...</div>}

        {!isLoading && filteredExpenses.length === 0 && (
          <div className="text-center py-8 text-gray-500">No expense records found</div>
        )}

        {!isLoading && filteredExpenses.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Expense #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Trip</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attachment</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="font-medium">{expense.expenseNumber}</TableCell>
                    <TableCell>{format(parseISO(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>{expense.vehicleRegistration}</TableCell>
                    <TableCell>{expense.tripNumber || '-'}</TableCell>
                    <TableCell>{categoryLabels[expense.category]}</TableCell>
                    <TableCell>{expense.vendor || '-'}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(expense.amount)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(expense.status)}>{expense.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {expense.hasAttachment ? (
                        <button
                          onClick={() => {
                            setViewingAttachmentExpense(expense);
                            setIsAttachmentViewerOpen(true);
                          }}
                          className="text-primary hover:text-primary-dark flex items-center gap-1"
                          title="View attachment"
                        >
                          {expense.attachmentContentType?.startsWith('image/') ? (
                            <ImageIcon size={16} />
                          ) : (
                            <Paperclip size={16} />
                          )}
                          <span className="text-xs">View</span>
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleView(expense)}
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        {expense.status === ExpenseStatus.PENDING && (
                          <>
                            <button
                              onClick={() => handleEdit(expense)}
                              className="p-1 text-primary hover:bg-blue-50 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(expense)}
                              className="p-1 text-error hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsEditMode(false);
          setSelectedExpense(null);
          setAttachmentFile(null);
          reset();
        }}
        title={isEditMode ? 'Edit Expense' : 'Record Expense'}
        size="lg"
        footer={
          <>
            <Button 
              variant="secondary" 
              onClick={() => {
                setIsModalOpen(false);
                setIsEditMode(false);
                setSelectedExpense(null);
                setAttachmentFile(null);
                reset();
              }} 
              disabled={recordMutation.isPending || updateMutation.isPending || isUploading}
            >
              Cancel
            </Button>
            {/* @ts-expect-error - Form types work correctly at runtime */}
            <Button onClick={handleSubmit(onSubmit)} loading={recordMutation.isPending || updateMutation.isPending || isUploading}>
              {isUploading ? 'Uploading...' : isEditMode ? 'Update Expense' : 'Record Expense'}
            </Button>
          </>
        }
      >
        {/* @ts-expect-error - Form types work correctly at runtime */}
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle <span className="text-red-500">*</span>
              </label>
              <select
                {...register('vehicleId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green"
              >
                <option value="">Select Vehicle</option>
                {vehicles?.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.registrationNumber} - {v.make} {v.model}
                  </option>
                ))}
              </select>
              {errors.vehicleId && <p className="text-xs text-red-500 mt-1">{errors.vehicleId.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip (Optional)</label>
              <select
                {...register('tripId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green"
              >
                <option value="">No trip</option>
                {trips?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.tripNumber} - {t.origin} → {t.destination}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Expense Date"
              required
              type="date"
              error={errors.expenseDate?.message}
              {...register('expenseDate')}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                {...register('category')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-green"
              >
                {Object.entries(categoryLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category.message}</p>}
            </div>

            <Input
              label="Amount"
              required
              type="number"
              step="0.01"
              placeholder="0"
              error={errors.amount?.message}
              {...register('amount')}
            />

            <Input
              label="Vendor"
              placeholder="e.g., ABC Station"
              error={errors.vendor?.message}
              {...register('vendor')}
            />

            <Input
              label="Receipt Number"
              placeholder="Receipt reference"
              error={errors.receiptNumber?.message}
              {...register('receiptNumber')}
            />
          </div>

          <Input
            label="Description"
            placeholder="Describe the expense"
            error={errors.description?.message}
            {...register('description')}
          />

          <div className="flex items-center gap-2">
            <input type="checkbox" {...register('reimbursable')} className="rounded" />
            <label className="text-sm text-gray-700">Reimbursable</label>
          </div>

          <Input
            label="Notes"
            placeholder="Additional notes"
            error={errors.notes?.message}
            {...register('notes')}
          />

          {/* Maintenance-specific fields */}
          {(selectedCategory === ExpenseCategory.MAINTENANCE || selectedCategory === ExpenseCategory.REPAIR) && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-900 mb-3">Maintenance Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Odometer Reading (km)"
                  type="number"
                  placeholder="e.g., 50000"
                  {...register('odometerReading')}
                />
                <Input
                  label="Service Provider"
                  placeholder="e.g., Toyota Service Center"
                  {...register('serviceProvider')}
                />
                <Input
                  label="Service Provider Contact"
                  placeholder="e.g., +255 123 456 789"
                  {...register('serviceProviderContact')}
                />
                <Input
                  label="Next Service Due"
                  type="date"
                  {...register('nextServiceDue')}
                />
                <Input
                  label="Next Service Odometer (km)"
                  type="number"
                  placeholder="e.g., 55000"
                  {...register('nextServiceOdometer')}
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Performed</label>
                <textarea
                  {...register('workPerformed')}
                  rows={2}
                  placeholder="Describe the work performed..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Parts Replaced</label>
                <textarea
                  {...register('partsReplaced')}
                  rows={2}
                  placeholder="List parts replaced..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          )}

          {/* Insurance/LATRA-specific fields */}
          {(selectedCategory === ExpenseCategory.INSURANCE || selectedCategory === ExpenseCategory.LATRA) && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-900 mb-3">{selectedCategory === ExpenseCategory.INSURANCE ? 'Insurance' : 'LATRA'} Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Valid From"
                  type="date"
                  {...register('validFrom')}
                />
                <Input
                  label="Valid Until"
                  type="date"
                  {...register('validUntil')}
                />
                {selectedCategory === ExpenseCategory.INSURANCE && (
                  <>
                    <Input
                      label="Policy Number"
                      placeholder="e.g., INS-2024-001"
                      {...register('policyNumber')}
                    />
                    <Input
                      label="Insurance Provider"
                      placeholder="e.g., NIC Insurance"
                      {...register('insuranceProvider')}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {/* Attachment Upload */}
          {!isEditMode && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-900 mb-3">Attachment (Optional)</h4>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark cursor-pointer"
                />
              </div>
              {attachmentFile && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                  <Paperclip size={16} />
                  <span>{attachmentFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setAttachmentFile(null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, JPG, PNG (max 10MB)
              </p>
            </div>
          )}

          <button type="submit" className="hidden" />
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedExpense(null);
        }}
        title="Expense Details"
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>
        }
      >
        {selectedExpense && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Expense Number</p>
                <p className="font-medium">{selectedExpense.expenseNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <Badge variant={getStatusVariant(selectedExpense.status)}>{selectedExpense.status}</Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-medium">{format(parseISO(selectedExpense.expenseDate), 'MMM dd, yyyy')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Category</p>
                <p className="font-medium">{categoryLabels[selectedExpense.category]}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Vehicle</p>
                <p className="font-medium">{selectedExpense.vehicleRegistration}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Trip</p>
                <p className="font-medium">{selectedExpense.tripNumber || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className="font-medium text-lg">{formatCurrency(selectedExpense.amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Vendor</p>
                <p className="font-medium">{selectedExpense.vendor || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Receipt Number</p>
                <p className="font-medium">{selectedExpense.receiptNumber || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Reimbursable</p>
                <p className="font-medium">{selectedExpense.reimbursable ? 'Yes' : 'No'}</p>
              </div>
            </div>
            {selectedExpense.description && (
              <div>
                <p className="text-sm text-gray-600">Description</p>
                <p className="font-medium">{selectedExpense.description}</p>
              </div>
            )}
            {selectedExpense.notes && (
              <div>
                <p className="text-sm text-gray-600">Notes</p>
                <p className="font-medium">{selectedExpense.notes}</p>
              </div>
            )}
            {selectedExpense.hasAttachment && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Attachment</p>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {selectedExpense.attachmentContentType?.startsWith('image/') ? (
                      <ImageIcon size={16} className="text-blue-500" />
                    ) : (
                      <Paperclip size={16} className="text-red-500" />
                    )}
                    <span className="font-medium">{selectedExpense.attachmentFileName}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setViewingAttachmentExpense(selectedExpense);
                        setIsAttachmentViewerOpen(true);
                      }}
                      className="flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors text-sm"
                    >
                      <Eye size={14} />
                      View
                    </button>
                    <button
                      onClick={() => handleDownloadAttachment(selectedExpense)}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
                    >
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Maintenance Details */}
            {(selectedExpense.category === ExpenseCategory.MAINTENANCE || selectedExpense.category === ExpenseCategory.REPAIR) && 
             (selectedExpense.odometerReading || selectedExpense.serviceProvider || selectedExpense.workPerformed) && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-3">Maintenance Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedExpense.odometerReading && (
                    <div>
                      <p className="text-sm text-gray-600">Odometer Reading</p>
                      <p className="font-medium">{selectedExpense.odometerReading.toLocaleString()} km</p>
                    </div>
                  )}
                  {selectedExpense.serviceProvider && (
                    <div>
                      <p className="text-sm text-gray-600">Service Provider</p>
                      <p className="font-medium">{selectedExpense.serviceProvider}</p>
                    </div>
                  )}
                  {selectedExpense.serviceProviderContact && (
                    <div>
                      <p className="text-sm text-gray-600">Contact</p>
                      <p className="font-medium">{selectedExpense.serviceProviderContact}</p>
                    </div>
                  )}
                  {selectedExpense.nextServiceDue && (
                    <div>
                      <p className="text-sm text-gray-600">Next Service Due</p>
                      <p className="font-medium">{format(parseISO(selectedExpense.nextServiceDue), 'MMM dd, yyyy')}</p>
                    </div>
                  )}
                  {selectedExpense.nextServiceOdometer && (
                    <div>
                      <p className="text-sm text-gray-600">Next Service Odometer</p>
                      <p className="font-medium">{selectedExpense.nextServiceOdometer.toLocaleString()} km</p>
                    </div>
                  )}
                </div>
                {selectedExpense.workPerformed && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">Work Performed</p>
                    <p className="font-medium whitespace-pre-wrap">{selectedExpense.workPerformed}</p>
                  </div>
                )}
                {selectedExpense.partsReplaced && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">Parts Replaced</p>
                    <p className="font-medium whitespace-pre-wrap">{selectedExpense.partsReplaced}</p>
                  </div>
                )}
              </div>
            )}

            {/* Insurance/LATRA Details */}
            {(selectedExpense.category === ExpenseCategory.INSURANCE || selectedExpense.category === ExpenseCategory.LATRA) && 
             (selectedExpense.validFrom || selectedExpense.validUntil || selectedExpense.policyNumber) && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  {selectedExpense.category === ExpenseCategory.INSURANCE ? 'Insurance' : 'LATRA'} Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedExpense.validFrom && (
                    <div>
                      <p className="text-sm text-gray-600">Valid From</p>
                      <p className="font-medium">{format(parseISO(selectedExpense.validFrom), 'MMM dd, yyyy')}</p>
                    </div>
                  )}
                  {selectedExpense.validUntil && (
                    <div>
                      <p className="text-sm text-gray-600">Valid Until</p>
                      <p className="font-medium">{format(parseISO(selectedExpense.validUntil), 'MMM dd, yyyy')}</p>
                    </div>
                  )}
                  {selectedExpense.policyNumber && (
                    <div>
                      <p className="text-sm text-gray-600">Policy Number</p>
                      <p className="font-medium">{selectedExpense.policyNumber}</p>
                    </div>
                  )}
                  {selectedExpense.insuranceProvider && (
                    <div>
                      <p className="text-sm text-gray-600">Insurance Provider</p>
                      <p className="font-medium">{selectedExpense.insuranceProvider}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="border-t pt-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Recorded By</p>
                  <p className="font-medium">{selectedExpense.recordedByName || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Created At</p>
                  <p className="font-medium">{format(parseISO(selectedExpense.createdAt), 'MMM dd, yyyy HH:mm')}</p>
                </div>
                {selectedExpense.approvedByName && (
                  <>
                    <div>
                      <p className="text-gray-600">Approved By</p>
                      <p className="font-medium">{selectedExpense.approvedByName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Approved At</p>
                      <p className="font-medium">
                        {selectedExpense.approvedAt ? format(parseISO(selectedExpense.approvedAt), 'MMM dd, yyyy HH:mm') : '-'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Attachment Viewer */}
      {viewingAttachmentExpense && (
        <AttachmentViewer
          expenseId={viewingAttachmentExpense.id}
          fileName={viewingAttachmentExpense.attachmentFileName || 'attachment'}
          contentType={viewingAttachmentExpense.attachmentContentType || 'application/octet-stream'}
          isOpen={isAttachmentViewerOpen}
          onClose={() => {
            setIsAttachmentViewerOpen(false);
            setViewingAttachmentExpense(null);
          }}
          onDownload={() => handleDownloadAttachment(viewingAttachmentExpense)}
        />
      )}
    </div>
  );
};
